/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import minutemanLogo from "@/assets/images/minuteman-logo.png";
import targetIcon from "@/assets/images/target-icon.png";
import bgMinuteman from "@/assets/images/bg-minuteman.jpg";
import shootingSlider from "@/assets/images/shooting-slider-1024x284.jpg";
import minutemanClubSlider from "@/assets/images/MinuteManSportsmansClub-1024x284.jpg";
import becomeAMember from "@/assets/images/become-a-member.jpg";
import calendarOfEvents from "@/assets/images/Calendar-Of-Events-Minute-Man.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/wp-content/uploads/2017/04/minuteman-logo.png": minutemanLogo,
  "/assets/images/minuteman-logo.png": minutemanLogo,
  "/images/minuteman-logo.png": minutemanLogo,
  "/minuteman-logo.png": minutemanLogo,
  
  // Icon mappings
  "/wp-content/themes/minuteman/assets/images/target-icon.png": targetIcon,
  "/assets/images/target-icon.png": targetIcon,
  "/images/target-icon.png": targetIcon,
  "/target-icon.png": targetIcon,
  
  // Hero background images
  "/wp-content/uploads/2017/04/bg-minuteman.jpg": bgMinuteman,
  "/assets/images/bg-minuteman.jpg": bgMinuteman,
  "/images/bg-minuteman.jpg": bgMinuteman,
  "/bg-minuteman.jpg": bgMinuteman,
  
  // Hero slider images
  "/wp-content/uploads/2017/04/shooting-slider-1024x284.jpg": shootingSlider,
  "/assets/images/shooting-slider-1024x284.jpg": shootingSlider,
  "/images/shooting-slider-1024x284.jpg": shootingSlider,
  "/shooting-slider-1024x284.jpg": shootingSlider,
  
  "/wp-content/uploads/2017/06/MinuteManSportsmansClub-1024x284.jpg": minutemanClubSlider,
  "/assets/images/MinuteManSportsmansClub-1024x284.jpg": minutemanClubSlider,
  "/images/MinuteManSportsmansClub-1024x284.jpg": minutemanClubSlider,
  "/MinuteManSportsmansClub-1024x284.jpg": minutemanClubSlider,
  
  // Main content section images
  "/wp-content/uploads/2017/04/become-a-member.jpg": becomeAMember,
  "/assets/images/become-a-member.jpg": becomeAMember,
  "/images/become-a-member.jpg": becomeAMember,
  "/become-a-member.jpg": becomeAMember,
  
  "/wp-content/uploads/2017/03/Calendar-Of-Events-Minute-Man.jpg": calendarOfEvents,
  "/assets/images/Calendar-Of-Events-Minute-Man.jpg": calendarOfEvents,
  "/images/Calendar-Of-Events-Minute-Man.jpg": calendarOfEvents,
  "/Calendar-Of-Events-Minute-Man.jpg": calendarOfEvents,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
